import React, { useEffect } from "react";
import CustomDropdown from "../CustomDropdown";
import apiHooks from "../../customHooks/apis";
import {
  CustomerDetailsFormData,
  UpgradeAccountTypes,
  ValidateAccountResponse,
} from "../../types/upgradeAccount";
import { useFormik } from "formik";
import {
  initialCustomerAddressData,
  initialEmploymentData,
  salaryOptions,
} from "../../utils/constants";
import CustomInputField from "../CustomInputField/CustomInputField";
import SectionLabel from "../SectionLabel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { saveStepperData } from "../../redux/stepperSlice";
import { getCustomerDetailsValidationSchema } from "../../utils/validationSchemas";
import {
  extractValuesFromObj,
  isEmptyObj,
  returnInputErrorMessage,
} from "../../utils/helpers";

const CustomerDetails: React.FC<UpgradeAccountTypes> = ({ setCurrentStep }) => {
  const dispatch = useAppDispatch();
  const { tierType, ...rest } = useAppSelector((state) => state.stepper.data);
  const { useGetEmploymentInfo, useUserDetails } = apiHooks;
  const { employmentInfo, gettingEmploymentInfo } = useGetEmploymentInfo();
  const { userDetails } = useUserDetails<ValidateAccountResponse>();
  const [searchParams] = useSearchParams();
  const isTier3 = searchParams.get("type") === "tier3";
  const { firstName = "", lastName = "", middleName = "" } = userDetails;

  const initialFormValues = {
    ...initialEmploymentData,
    ...(tierType === 3 ? initialCustomerAddressData : {}),
  };
  const formik = useFormik<CustomerDetailsFormData>({
    initialValues: initialFormValues,
    validationSchema: getCustomerDetailsValidationSchema(tierType),
    onSubmit(values) {
      dispatch(saveStepperData<CustomerDetailsFormData>({ ...values }));
      setCurrentStep(2);
    },
  });
  const {
    values,
    handleChange,
    handleSubmit,
    isValid,
    errors,
    setValues,
    initialValues,
    touched,
  } = formik;

  useEffect(() => {
    setValues(
      extractValuesFromObj(initialFormValues, rest) as CustomerDetailsFormData,
    );
  }, []);

  useEffect(() => {
    if (!isEmptyObj(values)) {
      const copyObj = { ...values };
      if (copyObj.selectEmploymentStatus === "Employed") {
        copyObj.businessAddress = "";
        copyObj.natureofBusiness = "";
        dispatch(saveStepperData(copyObj));
      } else if (copyObj.selectEmploymentStatus.includes("Self")) {
        copyObj.positionHeld = "";
        copyObj.employerAddress = "";
        copyObj.salaryInfo = "";
        copyObj.employerName = "";
        dispatch(saveStepperData(copyObj));
      } else {
        copyObj.positionHeld = "";
        copyObj.employerAddress = "";
        copyObj.salaryInfo = "";
        copyObj.employerName = "";
        copyObj.businessAddress = "";
        copyObj.natureofBusiness = "";
        dispatch(saveStepperData(copyObj));
      }
    }
  }, [values.selectEmploymentStatus]);

  return (
    <div>
      <h3 className="fw-semibold fs-5 text-center">Employment Details</h3>
      <p className="text-center">
        Hello{" "}
        <strong className="text-capitalize">
          {(firstName || middleName || lastName || "Customer").toLowerCase()}
        </strong>
        , Welcome to The Alternative Bank
      </p>

      <form className="d-flex gap-3 flex-column" onSubmit={handleSubmit}>
        {isTier3 && (
          <>
            <SectionLabel label={"Residential address"} />
            <div className="row">
              <CustomInputField className="col-6" label="House number" />
              <CustomInputField className="col-6" label="Street name" />
            </div>
            <div className="row">
              <CustomInputField
                className="col-6"
                label="Nearest bus-stop/landmark"
              />
              <CustomInputField className="col-6" label="Local Government" />
            </div>
            <div className="row">
              <CustomInputField className="col-6" label="Town" />
              <CustomInputField className="col-6" label="State" />
            </div>
            <CustomInputField label="Full address" />
          </>
        )}
        <SectionLabel label={"Employment Information"} />
        <b className="fs-5">Provide employment details and documents.</b>
        <p>
          Kindly provide your employment details and upload the necessary
          documents to continue your account upgrade.
        </p>
        <CustomDropdown
          label="Select employment information"
          options={employmentInfo}
          labelKey="name"
          valueKey="name"
          disabled={gettingEmploymentInfo}
          onClickOption={handleChange}
          value={values.selectEmploymentStatus||''}
          name="selectEmploymentStatus"
          formik={formik}
          required
        />
        {values.selectEmploymentStatus === "Employed" && (
          <div className="d-grid gap-3">
            <div className="row align-items-end">
              <CustomInputField
                className="col"
                label="Employer Name"
                name="employerName"
                value={values.employerName || ""}
                onChange={handleChange}
                errorMessage={returnInputErrorMessage<CustomerDetailsFormData>(
                  formik,
                  "employerName",
                )}
                required
              />
              <CustomDropdown
                extraClasses="col"
                label="Salary Information"
                options={salaryOptions}
                formik={formik}
                onClickOption={handleChange}
                value={values.salaryInfo||''}
                name="salaryInfo"
                required
              />
            </div>
            <CustomInputField
              label="Employer Address"
              name="employerAddress"
              onChange={handleChange}
              value={values.employerAddress}
              errorMessage={returnInputErrorMessage<CustomerDetailsFormData>(
                formik,
                "employerAddress",
              )}
              required
            />
            <CustomInputField
              label="Position Held"
              name="positionHeld"
              onChange={handleChange}
              value={values.positionHeld}
              errorMessage={returnInputErrorMessage<CustomerDetailsFormData>(
                formik,
                "positionHeld",
              )}
              required
            />
          </div>
        )}
        {values.selectEmploymentStatus === "Self employed" && (
          <div className="d-grid gap-3">
            <CustomInputField
              label="Nature of Business"
              name="natureofBusiness"
              required
              onChange={handleChange}
              value={values.natureofBusiness}
              errorMessage={returnInputErrorMessage<CustomerDetailsFormData>(
                formik,
                "natureofBusiness",
              )}
            />
            <CustomInputField
              label="Business Address"
              name="businessAddress"
              onChange={handleChange}
              value={values.businessAddress}
              errorMessage={returnInputErrorMessage<CustomerDetailsFormData>(
                formik,
                "businessAddress",
              )}
              required
            />
          </div>
        )}
        <div className="d-inline-block ms-auto">
          <button
            className="whiteBtn"
            type="button"
            onClick={() => setCurrentStep(0)}
          >
            Previous
          </button>
          <button
            type="submit"
            className={`coloredBtn ms-4 ${!isValid ? "disabled" : ""}`}
            disabled={!isValid}
          >
            Proceed
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerDetails;
